import { Info, LocalOfferOutlined } from '@mui/icons-material';
import { Box, Typography, useTheme } from '@mui/material';
import {
  EditAppBar,
  NonEditableField,
  NotificationTypeEnum,
  OneMedia,
  PageWithDrawer,
  useAdvice,
  useNotification,
} from '@prismamedia/one-components';
import { isEqual } from 'lodash';
import {
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  BrandKey,
  GetTagForTagEditQuery_query_tag_Tag_brandedContents_TagBrandedContent,
} from '../../__generated__/queries-web';
import { Draft } from '../../components/Draft';
import { actions as DraftActions } from '../../components/Draft/DraftActions';
import { MediaInput } from '../../components/MediaInput';
import { MEDIA_TYPES } from '../../components/MediaInput/constants';
import { TagIcons } from '../../components/Tags/TagItem/Thumbnail/constants';
import { paths } from '../../routing/Router/paths';
import { formatDate } from '../../utils/dateUtils';
import { AdvicePopper } from '../ArticleEdit/ArticleEditor/AdvicePopper';
import { useGetTag, useUpdateBrandedContent } from './tagEdit.web.graphql';

export const TagEdit: FC = () => {
  const history = useHistory();
  const theme = useTheme();
  const { pushNotification } = useNotification();
  const { openAdvice } = useAdvice();
  const textFieldRef = useRef(null);
  const [brandedContent, setBrandedContent] = useState<
    | GetTagForTagEditQuery_query_tag_Tag_brandedContents_TagBrandedContent
    | undefined
  >(undefined);
  const { id, brandKey } = useParams<{ id: string; brandKey: BrandKey }>();
  const { data, loading, error } = useGetTag({ id, brandKey });
  const dataBrandedContent = data?.tag?.brandedContents[0];
  const [saveLoading, setSaveLoading] = useState(false);
  const [updateBrandedContent] = useUpdateBrandedContent();
  const itemDiff = useMemo(() => !isEqual(brandedContent, dataBrandedContent), [
    brandedContent,
    dataBrandedContent,
  ]);
  const Icon = (data?.tag && TagIcons[data?.tag.type]) || LocalOfferOutlined;

  const media = useMemo(() => {
    try {
      return brandedContent?.media || null;
    } catch (e) {
      return null;
    }
  }, [brandedContent?.media]);

  const setMedia = (setter: SetStateAction<OneMedia[]>) => {
    const newMedia =
      typeof setter === 'function' ? setter(media ? [media] : []) : setter;
    setBrandedContent(
      (prev) =>
        prev && {
          ...prev,
          media: newMedia[0] || null,
        },
    );
  };

  useEffect(() => {
    dataBrandedContent && setBrandedContent(dataBrandedContent);
  }, [dataBrandedContent]);

  const onSave = async () => {
    if (data?.tag) {
      setSaveLoading(true);
      try {
        const body =
          typeof brandedContent?.body === 'string' && brandedContent?.body
            ? JSON.parse(brandedContent.body)
            : brandedContent?.body;

        await updateBrandedContent({
          variables: {
            where: { id: brandedContent?.id },
            data: { body, media: brandedContent?.media },
          },
        });
        pushNotification({
          type: NotificationTypeEnum.success,
          message: 'Tag enregistré',
        });
      } catch (e) {
        pushNotification({
          message: (e as Error).message,
          type: NotificationTypeEnum.error,
        });
      }
      setSaveLoading(false);
    }
  };

  const handleInfoIconClick = useCallback(
    (anchorRef, content) => () => {
      openAdvice({
        anchorRef,
        content,
        PopperProps: {
          keepMounted: false,
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundariesElement: 'window',
                enabled: false,
                escapeWithReference: true,
              },
            },
          ],
          style: { width: '310px' },
        },
        zIndex: 1,
      });
    },
    [openAdvice],
  );

  return (
    <>
      <EditAppBar
        title={data?.tag?.title}
        onNavigateBack={() => history.push(paths.TAG_LIST)}
        save={{
          disabled: !itemDiff,
          loading: saveLoading,
          onClick: onSave,
        }}
      />
      <PageWithDrawer
        loading={loading}
        error={error || (!data?.tag ? Error("Ce tag n'existe pas") : undefined)}
        rightDrawer={
          <>
            <NonEditableField
              label="Id du tag"
              value={data?.tag?.id}
              sx={{ mb: 2 }}
            />
            <NonEditableField label="Marque" value={brandKey} sx={{ mb: 2 }} />
            <NonEditableField
              label="Statut"
              value={
                <Typography
                  sx={{
                    color: brandedContent?.isActive
                      ? undefined
                      : theme.palette.error.main,
                  }}
                >
                  {brandedContent?.isActive ? 'Actif' : 'Inactif'}
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            <NonEditableField
              label="Nombre d'articles liés"
              value={data?.tag?.articleUsages[0]?.total}
              sx={{ mb: 2 }}
            />
            <NonEditableField
              label="Type"
              value={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 16, mr: 1 }} />
                  <Typography>{data?.tag?.type}</Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />
            <NonEditableField
              label="Langue"
              value={data?.tag?.lang}
              sx={{ mb: 2 }}
            />
            {dataBrandedContent?.createdAt && (
              <NonEditableField
                label="Date de création"
                value={formatDate(
                  dataBrandedContent?.createdAt,
                  'dd/MM/yyyy à HH:mm',
                )}
                sx={{ mb: 2 }}
              />
            )}
            {dataBrandedContent?.updatedAt && (
              <NonEditableField
                label="Date de modification"
                value={formatDate(
                  dataBrandedContent?.updatedAt,
                  'dd/MM/yyyy à HH:mm',
                )}
                sx={{ mb: 2 }}
              />
            )}
          </>
        }
      >
        <MediaInput
          medias={media ? [media] : []}
          setMedias={setMedia}
          brandKey={brandKey}
          allowedMedias={[MEDIA_TYPES.IMAGE]}
          sx={{ mb: 4 }}
        />
        <Draft
          rawDraft={brandedContent?.body}
          onChange={(value) =>
            setBrandedContent((prev) => prev && { ...prev, body: value })
          }
          actions={[
            DraftActions.BOLD,
            DraftActions.ITALIC,
            DraftActions.LINK,
            DraftActions.TITLE,
          ]}
          placeholder={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box>Texte</Box>
              <Info
                sx={{ marginLeft: '0.5rem', color: 'black' }}
                onClick={handleInfoIconClick(
                  textFieldRef,
                  <AdvicePopper
                    accordions={[
                      {
                        details: `Les pages de tags permettent de regrouper les articles traitant d'une même thématique.\nLe texte d'introduction de chaque page tag doit présenter cette thématique et encourager les internautes à explorer les articles associés.\nLe contenu doit rester sous la limite de 300 caractères, en étant concis et engageant pour le lecteur.`,
                        summary: 'Texte inférieur à 300 caractères',
                      },
                    ]}
                    title="Conseil de saisie"
                  />,
                )}
                ref={textFieldRef}
              />
            </Box>
          }
        />
      </PageWithDrawer>
    </>
  );
};
